import { useEffect, useState } from 'react';

// Register service worker
export const registerServiceWorker = async () => {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/service-worker.js', {
        scope: '/'
      });
      console.log('Service Worker registered:', registration.scope);

      // Iter-180 P0 — when a new SW is installed (e.g. after the v1→v2
      // cache bump ships to production), force the new one to take over
      // immediately and reload the page once. Without this, mobile
      // users can keep running stale cached bundles for hours/days.
      // Iter-197 — DO NOT auto-SKIP_WAITING anymore. The
      // <UpdateAvailableToast /> component now owns that decision so
      // mid-apply / mid-checkout users aren't reload-bombed. We still
      // listen for `updatefound` to know a new SW is staged, but we
      // let the SW broadcast SW_UPDATE_AVAILABLE on activate (which
      // happens only after the user clicks Refresh OR after an idle
      // grace period the toast enforces).
      registration.addEventListener('updatefound', () => {
        const installing = registration.installing;
        if (!installing) return;
        installing.addEventListener('statechange', () => {
          if (installing.state === 'installed' && navigator.serviceWorker.controller) {
            // New worker is now waiting. The toast component will catch
            // the eventual SW_UPDATE_AVAILABLE broadcast and surface
            // the refresh prompt.
            console.log('[SW] update available — deferring to UpdateAvailableToast');
          }
        });
      });
      let reloaded = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (reloaded) return;
        reloaded = true;
        window.location.reload();
      });

      // Iter-197 — Background poll for new SW. Browsers re-fetch the
      // service-worker.js file on navigation but not on long-lived
      // SPA sessions. A 15-minute update check keeps users current
      // even if they never hard-navigate.
      setInterval(() => {
        try { registration.update(); } catch (_) { /* noop */ }
      }, 15 * 60 * 1000);

      // Iter-229h — capture the SW version on window so the JobsV2
      // empty-state diagnostic can include which SW (and therefore
      // which JS chunk vintage) the user is actually running. The SW
      // exposes a GET_VERSION message handler.
      try {
        const controller = navigator.serviceWorker.controller || registration.active || registration.waiting;
        if (controller) {
          const channel = new MessageChannel();
          channel.port1.onmessage = (evt) => {
            if (evt.data?.type === 'SW_VERSION') {
              window.__SW_VERSION__ = evt.data.version || 'unknown';
            }
          };
          controller.postMessage({ type: 'GET_VERSION' }, [channel.port2]);
        }
        navigator.serviceWorker.addEventListener('message', (evt) => {
          if (evt.data?.type === 'SW_UPDATE_AVAILABLE' || evt.data?.type === 'SW_VERSION') {
            window.__SW_VERSION__ = evt.data.version || window.__SW_VERSION__ || 'unknown';
          }
        });
      } catch (_) { /* noop */ }

      return registration;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
    }
  }
  return null;
};

// Hook to handle PWA install prompt
export const usePWAInstall = () => {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Listen for install prompt
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      setIsInstallable(true);
    };

    // Listen for successful install
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setInstallPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const promptInstall = async () => {
    if (!installPrompt) return false;

    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setIsInstalled(true);
    }
    
    setInstallPrompt(null);
    setIsInstallable(false);
    
    return outcome === 'accepted';
  };

  return {
    isInstallable,
    isInstalled,
    promptInstall
  };
};

export default registerServiceWorker;
